import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { map } from 'rxjs/operators'
import { environment as ENV } from '../../../environments/environment'
import { ApiResponse } from '../model/api.model'
import { catchAndHandleError, mapToMaybeData } from '../util'
import { ProgramSelection } from 'src/app/submit-to-market/components/program-selector/program-selector.component'
import {
  CreateNewDraft,
  RecentDraft,
  MarketLayerMapping,
  FilteredSelection,
  DraftUpdateStatus,
} from 'src/app/submit-to-market/models/draft-selection.model'
import {
  AttachmentMapping,
  DraftAndPortfolioData,
  DraftDashboardListItem,
  EmailTemplateMapping,
  EmailTemplate,
  EmailAndQuoteCreationResult,
  Contact,
  StructuresOverride,
  UpdateStructuresOverrideRequest,
} from 'src/app/submit-to-market/models/draft-data.model'
import { UploadAttachment } from 'src/app/submit-to-market/models/attachments.model'
import { CatModelingWorkspaceFile } from 'src/app/cat-modeling/model/cat-modeling-workspace.model'
import { EmailDto } from 'src/app/submit-to-market/models/send-model'
import { STMMarketOverride } from 'src/app/submit-to-market/models/draft.model'
import { STMSageLinkDisplayResponse } from 'src/app/submit-to-market/models/submit-to-market-sage-link.model'

const API = ENV.internalApi

enum SubmitToMarketRequests {
  FetchDraftList = 'Fetch Draft List',
  FetchCodeMappingList = 'Fetch Code Mapping List',
  FetchPreviousMarkets = 'Fetch Previous Markets',
  FetchDraftData = 'Fetch Draft Data',
  FetchSTMDATA = 'Fetch STM Data',
  FetchBrokers = 'Fetch All Brokers',
  DeleteDraftData = 'Fetch Draft Data',
  CreateNewDraft = 'Create new Draft',
  UpdateDraftDashboards = 'Update Draft Dashboards',
}

enum SubmitToMarketApiRoutes {
  Drafts = '/drafts',
  Combined = '/combined',
  Status = '/status',
  Structures = '/structures',
  Markets = '/markets',
  Dashboards = '/dashboards',
  Attachments = '/attachments',
  CatModelingAttachments = '/attachments/cat-modeling',
  CatModelingEntries = '/attachments/cat-modeling-entries',
  Templates = '/templates',
  SendEmails = '/send-emails',
  CreatePanels = '/create-panels',
  Overrides = '/overrides',
  Brokers = '/brokers',
  STMData = '/stm-data',
  PreviousMarkets = '/previous-markets',
}

enum SubmitToMarketApiParams {
  PageSize = 'pageSize',
  PageOffset = 'pageOffset',
}

@Injectable({
  providedIn: 'root',
})
export class SubmitToMarketAPIService {
  private http = inject(HttpClient)

  createNewDraft(draftData: CreateNewDraft): ApiResponse<RecentDraft> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}`
    return this.postEntity<CreateNewDraft, RecentDraft>(
      draftData,
      endpoint,
      SubmitToMarketRequests.CreateNewDraft,
    )
  }

  updateDraftAttachmentMappping(
    draftId: number,
    attachmentMapping: AttachmentMapping[],
  ): ApiResponse<DraftAndPortfolioData> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}/${draftId}${SubmitToMarketApiRoutes.Attachments}`
    return this.patchEntity<AttachmentMapping[], DraftAndPortfolioData>(
      attachmentMapping,
      endpoint,
      SubmitToMarketRequests.CreateNewDraft,
    )
  }

  createDraftTemplate(draftId: number): ApiResponse<DraftAndPortfolioData> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}/${draftId}${SubmitToMarketApiRoutes.Templates}`
    return this.postEntity<object, DraftAndPortfolioData>(
      {},
      endpoint,
      SubmitToMarketRequests.CreateNewDraft,
    )
  }

  updateDraftDashboard(
    draftId: number,
    dashboards: DraftDashboardListItem[],
  ): ApiResponse<DraftAndPortfolioData> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}/${draftId}${SubmitToMarketApiRoutes.Dashboards}`
    return this.patchEntity<DraftDashboardListItem[], DraftAndPortfolioData>(
      dashboards,
      endpoint,
      SubmitToMarketRequests.UpdateDraftDashboards,
    )
  }

  updateDraftMarketMappping(
    draftId: number,
    layerMarketMapping: MarketLayerMapping[],
  ): ApiResponse<DraftAndPortfolioData> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}/${draftId}${SubmitToMarketApiRoutes.Markets}`
    return this.patchEntity<MarketLayerMapping[], DraftAndPortfolioData>(
      layerMarketMapping,
      endpoint,
      SubmitToMarketRequests.CreateNewDraft,
    )
  }

  updateDraftEmailTemplates(
    draftId: number,
    emailTemplates: EmailTemplate[],
    emailTemplateMapping: EmailTemplateMapping[],
  ): ApiResponse<DraftAndPortfolioData> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}/${draftId}${SubmitToMarketApiRoutes.Templates}`
    const data = { templates: emailTemplates, mapping: emailTemplateMapping }
    return this.patchEntity<typeof data, DraftAndPortfolioData>(
      data,
      endpoint,
      SubmitToMarketRequests.CreateNewDraft,
    )
  }

  uploadDraftAttachments(
    draftId: number,
    attachments: UploadAttachment[],
  ): ApiResponse<DraftAndPortfolioData> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}/${draftId}${SubmitToMarketApiRoutes.Attachments}`
    const formData = new FormData()

    attachments.forEach((attachment, index) => {
      formData.append('files', attachment.data, attachment.attachmentName)
      formData.append(`attachmentNames[${index}]`, attachment.attachmentName)
    })

    return this.postEntity<FormData, DraftAndPortfolioData>(
      formData,
      endpoint,
      SubmitToMarketRequests.CreateNewDraft,
    )
  }

  uploadCatModelingFilesAsAttachments(
    draftId: number,
    fileIdsToAttach: number[],
    fileIdsToDetach: number[],
  ): ApiResponse<DraftAndPortfolioData> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}/${draftId}${SubmitToMarketApiRoutes.CatModelingAttachments}`
    return this.postEntity<
      { fileIdsToAttach: number[]; fileIdsToDetach: number[] },
      DraftAndPortfolioData
    >({ fileIdsToAttach, fileIdsToDetach }, endpoint, SubmitToMarketRequests.CreateNewDraft)
  }

  uploadCatModelingEntriesAsAttachments(
    draftId: number,
    entryIdsToAttach: number[],
    entryIdsToDetach: number[],
  ): ApiResponse<DraftAndPortfolioData> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}/${draftId}${SubmitToMarketApiRoutes.CatModelingEntries}`
    return this.postEntity<
      { entryIdsToAttach: number[]; entryIdsToDetach: number[] },
      DraftAndPortfolioData
    >({ entryIdsToAttach, entryIdsToDetach }, endpoint, SubmitToMarketRequests.CreateNewDraft)
  }

  updateDraftStructures(
    draftId: number,
    structuresIds: number[],
  ): ApiResponse<DraftAndPortfolioData> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}/${draftId}${SubmitToMarketApiRoutes.Structures}`
    return this.patchEntity<number[], DraftAndPortfolioData>(
      structuresIds,
      endpoint,
      SubmitToMarketRequests.CreateNewDraft,
    )
  }

  updateMappingOverrides(
    draftId: number,
    overrides: STMMarketOverride[],
  ): ApiResponse<DraftAndPortfolioData> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}/${draftId}${SubmitToMarketApiRoutes.Markets}${SubmitToMarketApiRoutes.Overrides}`
    return this.patchEntity<STMMarketOverride[], DraftAndPortfolioData>(
      overrides,
      endpoint,
      SubmitToMarketRequests.CreateNewDraft,
    )
  }

  getDraftData(draftId: number): ApiResponse<DraftAndPortfolioData> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}/${draftId}`
    return this.getEntity<DraftAndPortfolioData>(endpoint, SubmitToMarketRequests.FetchDraftData)
  }

  getSTMDataToDisplay(
    draftId: number,
    queryString?: string,
  ): ApiResponse<STMSageLinkDisplayResponse[]> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}/${draftId}${SubmitToMarketApiRoutes.STMData}${queryString ? `?${queryString}` : ''}`
    return this.getEntity<STMSageLinkDisplayResponse[]>(
      endpoint,
      SubmitToMarketRequests.FetchSTMDATA,
    )
  }

  getAllBrokers(): ApiResponse<Contact[]> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Brokers}`
    return this.getEntity<Contact[]>(endpoint, SubmitToMarketRequests.FetchBrokers)
  }

  sendEmails(draftId: number, emails: EmailDto[]): ApiResponse<number[]> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}/${draftId}${SubmitToMarketApiRoutes.SendEmails}`
    return this.postEntity<EmailDto[], number[]>(
      emails,
      endpoint,
      SubmitToMarketRequests.FetchDraftData,
    )
  }

  createQuotePanels(
    draftId: number,
    reinsurerIds: number[],
  ): ApiResponse<EmailAndQuoteCreationResult> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}/${draftId}${SubmitToMarketApiRoutes.CreatePanels}`
    return this.postEntity<number[], EmailAndQuoteCreationResult>(
      reinsurerIds,
      endpoint,
      SubmitToMarketRequests.FetchDraftData,
    )
  }

  deleteDraft(draftId: number): ApiResponse<void> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}/${draftId}`
    return this.deleteEntity<void>(endpoint, SubmitToMarketRequests.DeleteDraftData)
  }

  getCombinedDraftList(
    selection: FilteredSelection,
    pageOffset: number,
    pageSize: number = 50,
  ): ApiResponse<RecentDraft[]> {
    const queryParams = this.buildQueryParams([
      { key: 'yearIds', value: selection.year?.join(',') },
      { key: 'clientId', value: selection.clientId },
      { key: 'pageOffset', value: pageOffset },
      { key: 'pageSize', value: pageSize },
    ])
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}${SubmitToMarketApiRoutes.Combined}`
    return this.getEntity<RecentDraft[]>(
      endpoint,
      SubmitToMarketRequests.FetchDraftList,
      queryParams,
    )
  }

  getRecentDraftStatus(drafts: RecentDraft[]): ApiResponse<DraftUpdateStatus[]> {
    const queryParams = this.buildQueryParams([
      { key: 'draftIds', value: drafts.map((x) => x.id).join(',') },
    ])
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}${SubmitToMarketApiRoutes.Status}`
    return this.getEntity<DraftUpdateStatus[]>(
      endpoint,
      SubmitToMarketRequests.FetchDraftList,
      queryParams,
    )
  }

  getFilteredDraftList(
    selection: ProgramSelection,
    pageOffset: number,
    pageSize: number = 50,
  ): ApiResponse<RecentDraft[]> {
    const queryParams = this.buildQueryParams([
      { key: 'ProgramId', value: selection.programId },
      { key: 'YearId', value: selection.yearId },
      { key: 'ClientId', value: selection.clientId },
      { key: 'pageOffset', value: pageOffset },
      { key: 'pageSize', value: pageSize },
    ])
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}`
    return this.getEntity<RecentDraft[]>(
      endpoint,
      SubmitToMarketRequests.FetchDraftList,
      queryParams,
    )
  }

  private getEntity<T>(
    endpoint: string,
    requestType: SubmitToMarketRequests,
    queryParams?: string,
  ): ApiResponse<T> {
    const params = queryParams ? `?${queryParams}` : ''
    const url = `${API.base}${endpoint}${params}`
    return this.http.get<T>(url).pipe(mapToMaybeData(), catchAndHandleError(`GET ${requestType}`))
  }
  /**
   * @param T type getting mapped to
   * @param U type getting mapped from
   */
  private getEntityWithMapping<T, U>(
    endpoint: string,
    requestType: SubmitToMarketRequests,
    mapCustom: (data: U) => T,
    queryParams?: string,
  ): ApiResponse<T> {
    const params = queryParams ? `?${queryParams}` : ''
    const url = `${API.base}${endpoint}${params}`
    return this.http.get<U>(url).pipe(
      map((data: U) => mapCustom(data)),
      mapToMaybeData(),
      catchAndHandleError(`GET ${requestType}`),
    )
  }
  private postEntity<T, R>(
    entity: T,
    endpoint: string,
    requestType: SubmitToMarketRequests,
    queryParams?: string,
  ): ApiResponse<R> {
    const params = queryParams ? `?${queryParams}` : ''
    const url = `${API.base}${endpoint}${params}`
    return this.http
      .post<R>(url, entity)
      .pipe(mapToMaybeData(), catchAndHandleError(`POST ${requestType}`))
  }
  private putEntity<T>(
    entity: T,
    endpoint: string,
    requestType: SubmitToMarketRequests,
  ): ApiResponse<T> {
    const url = `${API.base}${endpoint}`
    return this.http
      .put<T>(url, entity)
      .pipe(mapToMaybeData(), catchAndHandleError(`PUT ${requestType}`))
  }

  private patchEntity<T, R>(
    entity: T,
    endpoint: string,
    requestType: SubmitToMarketRequests,
    queryParams?: string,
  ): ApiResponse<R> {
    const params = queryParams ? `?${queryParams}` : ''
    const url = `${API.base}${endpoint}${params}`
    return this.http
      .patch<R>(url, entity, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .pipe(mapToMaybeData(), catchAndHandleError(`POST ${requestType}`))
  }
  private deleteEntity<T>(endpoint: string, requestType: SubmitToMarketRequests): ApiResponse<T> {
    const url = `${API.base}${endpoint}`
    return this.http
      .delete<T>(url)
      .pipe(mapToMaybeData(), catchAndHandleError(`DELETE ${requestType}`))
  }
  private buildQueryParams(
    kvps: { key: string; value: string | number | boolean | undefined | null }[],
  ): string {
    return kvps
      .filter((kvp) => kvp.value !== undefined && kvp.value !== null && kvp.value !== '')
      .map((kvp) => this.buildQueryParam(kvp.key, kvp.value))
      .join('&')
  }
  private buildQueryParam(
    key: string,
    value: string | number | boolean | undefined | null,
  ): string {
    return value === undefined || value === null ? '' : `${key}=${value}`
  }

  getCatFilesSubmittedToSageLinkForDraft(draftId: number): ApiResponse<number[]> {
    const endpoint = `/submit-to-market${SubmitToMarketApiRoutes.Drafts}/${draftId}/cat-files-submitted-to-sagelink`
    return this.getEntity<number[]>(endpoint, SubmitToMarketRequests.FetchDraftData)
  }

  upsertStructuresOverride(
    draftId: number,
    request: UpdateStructuresOverrideRequest,
  ): ApiResponse<DraftAndPortfolioData> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}/${draftId}/structures-overrides`
    return this.postEntity<UpdateStructuresOverrideRequest, DraftAndPortfolioData>(
      request,
      endpoint,
      SubmitToMarketRequests.FetchDraftData,
    )
  }

  deleteStructuresOverride(
    draftId: number,
    overrideId: number,
  ): ApiResponse<DraftAndPortfolioData> {
    const endpoint = `${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}/${draftId}/structures-overrides/${overrideId}`
    return this.deleteEntity<DraftAndPortfolioData>(endpoint, SubmitToMarketRequests.FetchDraftData)
  }

  bulkDeleteStructuresOverrides(
    draftId: number,
    programFactorId: number,
    templateId: number,
  ): ApiResponse<DraftAndPortfolioData> {
    const url = `${API.base}${API.submitToMarket}${SubmitToMarketApiRoutes.Drafts}/${draftId}/structures-overrides`
    return this.http
      .delete<DraftAndPortfolioData>(url, { body: { programFactorId, templateId } })
      .pipe(mapToMaybeData(), catchAndHandleError(`DELETE structures-overrides bulk`))
  }
}
